import {
  FilterGroup,
  FilterGroupOption,
} from "@components/common/data-grid/adm-data-grid-types";
import { AdmPlan } from "@custom-types/base-types";
import { blue, green, grey, red, yellow } from "@flat-ui";
import { GridFilterOperator } from "@mui/x-data-grid-pro";
import {
  isFeaturePlan,
  isSceneSeatPlan,
  isSubscriptionPlan,
  isTokenPlan,
} from "@utils/plan-utils";

/** Operator registered on the status column, keeping the plans whose status is any of the selected ones */
const STATUS_OPERATOR = "isAnyOfStatus";

/** Operator registered on the plan column, keeping the plans whose kind is any of the selected ones */
const TYPE_OPERATOR = "isAnyOfType";

/** The kinds of plan the Type filter can tell apart */
type PlanTypeId = "subscription" | "feature" | "tokens" | "sceneSeat";

/**
 * Maps each Type filter option to the predicate that recognizes a plan of that kind. The
 * subscription predicate excludes token plans, so that the three product kinds stay mutually
 * exclusive even though `isSubscriptionPlan` counts token plans as subscriptions too. The feature
 * predicate excludes SCENE seat plans for the same reason: `isFeaturePlan` counts them as features,
 * so without the exclusion the Feature and SCENE Seats options would not be distinguishable.
 */
const PLAN_TYPE_PREDICATES: Record<PlanTypeId, (plan: AdmPlan) => boolean> = {
  subscription: (plan) => isSubscriptionPlan(plan) && !isTokenPlan(plan),
  feature: (plan) => isFeaturePlan(plan) && !isSceneSeatPlan(plan),
  tokens: isTokenPlan,
  sceneSeat: isSceneSeatPlan,
};

/** @returns the value of a filter item as an array of option ids, or an empty array */
function toSelectedIds(value: unknown): string[] {
  return Array.isArray(value) ? (value as string[]) : [];
}

/**
 * Operator registered on the status column. A plan matches when its status is one of the selected
 * ones, giving the options of the Status dropdown an OR relationship.
 */
export const statusColumnFilterOperators: GridFilterOperator<{
  entity: AdmPlan;
}>[] = [
  {
    value: STATUS_OPERATOR,
    getApplyFilterFn: (filterItem) => {
      const selected = toSelectedIds(filterItem.value);
      if (selected.length === 0) {
        return null;
      }
      return (value) => typeof value === "string" && selected.includes(value);
    },
  },
];

/**
 * Operator registered on the plan column. A plan matches when it is of one of the selected kinds,
 * giving the options of the Type dropdown an OR relationship.
 *
 * It classifies a plan by its features and bundles, which the value based operators of the data
 * grid cannot do, as the plan column only holds the formatted plan name.
 */
export const planColumnFilterOperators: GridFilterOperator<{
  entity: AdmPlan;
}>[] = [
  {
    value: TYPE_OPERATOR,
    getApplyFilterFn: (filterItem) => {
      const selected = toSelectedIds(filterItem.value) as PlanTypeId[];
      if (selected.length === 0) {
        return null;
      }
      return (_, row) =>
        selected.some((typeId) => PLAN_TYPE_PREDICATES[typeId](row.entity));
    },
  },
];

/**
 * Options of the Status dropdown, available to everybody. Their colors mirror the status chips of
 * the plans table, so a status looks the same in the filter as it does in the table.
 */
const statusOptions: FilterGroupOption[] = [
  {
    id: "active",
    label: "Active",
    color: green[500],
    backgroundColor: green[100],
    isDefaultSelected: true,
  },
  {
    id: "invalid",
    label: "Invalid",
    color: blue[500],
    backgroundColor: blue[100],
    isDefaultSelected: true,
  },
  {
    id: "inactive",
    label: "Inactive",
    color: yellow[500],
    backgroundColor: yellow[100],
    isDefaultSelected: true,
  },
  {
    id: "deactivated",
    label: "Deactivated",
    color: red[500],
    backgroundColor: red[100],
  },
];

/**
 * Options of the Type dropdown, mirroring the split of the Add Subscription and Add Feature pages
 * so that a plan can be found under the same heading it was created under.
 */
const typeOptions: FilterGroupOption[] = [
  {
    id: "subscription",
    label: "Subscription",
    backgroundColor: grey[300],
    isDefaultSelected: true,
  },
  {
    id: "feature",
    label: "Feature",
    backgroundColor: grey[300],
    isDefaultSelected: true,
  },
  {
    id: "tokens",
    label: "Tokens",
    backgroundColor: grey[300],
    isDefaultSelected: true,
  },
];

/**
 * The SCENE seats option that tells the SCENE seats of a workspace apart from the plans they belong
 * to. It is only useful while supporting a customer, so it is added to the Type dropdown in dev mode
 * only.
 */
const sceneSeatOption: FilterGroupOption = {
  id: "sceneSeat",
  label: "SCENE Seats",
  backgroundColor: grey[300],
};

/** @returns the filter groups to offer in the plans table */
export function getPlansTableFilters({
  shouldShowTypeFilter,
  shouldShowDevModeFilters,
  onSelectSceneSeats,
}: {
  /**
   * Whether the Type dropdown should be offered. It acts on the plan column, which the hardware
   * licenses table has not.
   */
  shouldShowTypeFilter: boolean;

  /** Whether the Type options that are reserved for developers should be offered as well */
  shouldShowDevModeFilters: boolean;

  /**
   * Called when the SCENE Seats option is selected. SCENE seats live on individual members, so this
   * is used to reveal them by turning on the "Show all plans" experimental feature.
   */
  onSelectSceneSeats?: () => void;
}): FilterGroup[] {
  const statusGroup: FilterGroup = {
    id: "status",
    label: "Status",
    field: "status",
    operator: STATUS_OPERATOR,
    options: statusOptions,
  };

  if (!shouldShowTypeFilter) {
    return [statusGroup];
  }

  const typeGroup: FilterGroup = {
    id: "type",
    label: "Type",
    field: "plan",
    operator: TYPE_OPERATOR,
    options: [
      ...typeOptions,
      ...(shouldShowDevModeFilters
        ? [{ ...sceneSeatOption, onSelect: onSelectSceneSeats }]
        : []),
    ],
  };

  return [statusGroup, typeGroup];
}
